import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { ScopeCell } from '../components/ScopeCell';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });
const MONDAY0 = new Date(2026, 8, 7);
const DAY = { Mathematics: 0, 'English Language Arts': 1, Science: 2, 'Social Studies': 3, 'Cross-Disciplinary Application': 4 };
const isPast = (w, subject) => new Date(MONDAY0.getTime() + (w * 7 + (DAY[subject] || 0)) * 86400000) <= new Date();
const weekDate = (w) => new Date(MONDAY0.getTime() + w * 7 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function ScopeSequence() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [grade, setGrade] = useState(5);

  useEffect(() => {
    api.get('/academy/admin/scope-sequence', { headers: authHeaders() }).then((r) => setData(r.data)).catch(() => setData({ sequences: [] }));
  }, []);

  const seqs = (data?.sequences || []).filter((s) => s.grade === grade);

  const save = async (seq, items) => {
    try {
      const r = await api.put(`/academy/admin/scope-sequence/${seq.grade}/${encodeURIComponent(seq.subject)}`, { items }, { headers: authHeaders() });
      setData((d) => ({ ...d, sequences: d.sequences.map((x) => (x.grade === seq.grade && x.subject === seq.subject ? { ...x, items: r.data.items } : x)) }));
      toast.success(r.data.rebuilt_dates.length ? `Saved · ${r.data.rebuilt_dates.length} upcoming lesson day(s) will rebuild` : 'Saved');
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); return false; }
  };
  const editItem = (seq, week, f) => save(seq, seq.items.map((i) => (i.week === week ? { ...i, code: f.code, focus: f.focus } : i)));
  const moveItem = (seq, week, dir) => {
    const items = [...seq.items].sort((a, b) => a.week - b.week);
    const idx = items.findIndex((i) => i.week === week);
    const j = idx + dir;
    const a = items[idx]; const b = items[j];
    items[idx] = { ...a, code: b.code, focus: b.focus };
    items[j] = { ...b, code: a.code, focus: a.focus };
    return save(seq, items);
  };
  const weeks = [...new Set(seqs.flatMap((s) => s.items.map((i) => i.week)))].sort((a, b) => a - b);
  return (
    <div className="min-h-screen bg-black text-white px-4 py-6" data-testid="scope-page">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/academy/teacher')} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white mb-4" data-testid="scope-back"><ArrowLeft className="w-3.5 h-3.5" /> Teacher Portal</button>
        <h1 className="text-3xl font-black uppercase">Year Plan · {data?.school_year}</h1>
        <p className="text-xs text-zinc-500 mt-1 mb-5">One CA standard per subject per week, moving forward from Nov 2. Hover a week to edit it or move it earlier/later. Past weeks are locked; upcoming lessons rebuild automatically.</p>
        <div className="flex flex-wrap gap-1.5 mb-5">
          {[4, 5, 6, 7, 8, 9].map((g) => (
            <button key={g} onClick={() => setGrade(g)} className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest border-2 ${grade === g ? 'bg-orange-500 border-orange-500 text-black' : 'border-zinc-800 text-zinc-400'}`} data-testid={`scope-grade-${g}`}>Grade {g}</button>
          ))}
        </div>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-orange-500" /> : seqs.length === 0 ? (
          <div className="text-sm text-zinc-500" data-testid="scope-empty">The year plan for this grade is still being built. Check back in a few minutes.</div>
        ) : (
          <div className="overflow-x-auto border-2 border-zinc-800">
            <table className="w-full text-xs" data-testid="scope-table">
              <thead className="bg-zinc-950 text-zinc-500 uppercase tracking-widest text-[9px]">
                <tr><th className="text-left px-3 py-2">Week of</th>{seqs.map((s) => <th key={s.subject} className="text-left px-3 py-2">{s.subject}</th>)}</tr>
              </thead>
              <tbody>
                {weeks.map((w) => (
                  <tr key={w} className="border-t border-zinc-900">
                    <td className="px-3 py-2 text-zinc-400 whitespace-nowrap">{weekDate(w)}</td>
                    {seqs.map((s) => {
                      const sorted = [...s.items].sort((a, b) => a.week - b.week);
                      const idx = sorted.findIndex((i) => i.week === w);
                      const it = sorted[idx];
                      if (!it) return <td key={s.subject} />;
                      const canMove = (k) => k >= 0 && k < sorted.length && !isPast(sorted[k].week, s.subject);
                      return (
                        <td key={s.subject} className="px-3 py-2 align-top">
                          <ScopeCell key={`${it.code}-${it.focus}`} item={it} locked={isPast(w, s.subject)} canUp={canMove(idx - 1)} canDown={canMove(idx + 1)}
                            onMove={(dir) => moveItem(s, w, dir)} onSave={(f) => editItem(s, w, f)} testid={`scope-cell-${s.subject.split(' ')[0].toLowerCase()}-${w}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
